/**
 * The conversion API, as the browser sees it.
 *
 * Four calls and a poll. The plan itself never goes through the API: POST /uploads hands back
 * a presigned PUT and the browser sends the bytes straight to S3, because a sheet runs to tens
 * of megabytes and past the API Gateway payload limit. The recognition result and the finished
 * package come back the same way, as presigned GETs.
 */

import type { ConversionPackage } from "@plan-forge/conversion-package";
import type { JobRecord, RecognitionRequest } from "@plan-forge/job-contract";

/**
 * Set at build time. The dev API's own output; see infra/environments/dev. Exported so
 * speclens-api.ts builds URLs against the same base instead of reading the env var a second
 * time - both clients hit the same Lambda, so there is only one base to have.
 */
export const BASE = (import.meta.env["VITE_API_BASE_URL"] ?? "").replace(/\/$/, "");

/**
 * Supplies the Authorization header, refreshing the token if it is close to expiring.
 *
 * Passed in rather than read from a module-level variable, so there is exactly one place that
 * knows who is signed in and no way for two parts of the application to disagree about it.
 * Returns an empty object when auth is off, which is what lets the same client work against
 * an open dev API.
 */
export type AuthHeaders = () => Promise<Record<string, string>>;

const NO_AUTH: AuthHeaders = async () => ({});

export interface JobResponse extends JobRecord {
  /** Present once the job has succeeded. Expires; re-fetch the job for a fresh one. */
  readonly resultUrl?: string;
}

/** What the API refused, in the API's own words. */
export class ApiError extends Error {
  readonly errors: readonly string[];

  constructor(errors: readonly string[]) {
    // The API reports every problem with a request at once rather than only the first, so the
    // message keeps all of them - a form that fixes one error at a time is a form people
    // submit five times.
    super(errors.join("; "));
    this.name = "ApiError";
    this.errors = errors;
  }
}

/**
 * Exported for speclens-api.ts too - both clients talk to the same Lambda behind the same
 * BASE, so this response handling is one thing to keep right, not two.
 */
export async function json<T>(response: Response): Promise<T> {
  const text = await response.text();
  let body: unknown;
  try {
    body = text === "" ? {} : JSON.parse(text);
  } catch {
    throw new ApiError([text || response.statusText]);
  }
  if (!response.ok) {
    const errors = (body as { errors?: string[] }).errors;
    throw new ApiError(errors ?? [response.statusText]);
  }
  return body as T;
}

/**
 * A POST of JSON to our own API, authenticated. Exported for the same reason as {@link json}.
 */
export async function postJson<T>(path: string, body: unknown, auth: AuthHeaders): Promise<T> {
  return json<T>(
    await fetch(`${BASE}${path}`, {
      method: "POST",
      headers: { "content-type": "application/json", ...(await auth()) },
      body: JSON.stringify(body),
    }),
  );
}

export type AcceptedContentType = "application/pdf" | "image/png" | "image/jpeg";

/**
 * Uploads the plan and returns the key to name in {@link createJob}.
 *
 * `onProgress` exists because this is the only step whose duration depends on the file rather
 * than on the work: a 40 MB sheet over office wifi is a long silence otherwise.
 */
export async function uploadPlan(
  file: File,
  contentType: AcceptedContentType,
  onProgress?: (fraction: number) => void,
  auth: AuthHeaders = NO_AUTH,
): Promise<string> {
  const { key, url } = await postJson<{ key: string; url: string }>(
    "/uploads",
    { contentType },
    auth,
  );

  await new Promise<void>((resolve, reject) => {
    // XMLHttpRequest rather than fetch, only because fetch still cannot report upload
    // progress. Nothing else here wants it.
    const request = new XMLHttpRequest();
    request.open("PUT", url);
    // The content type is part of what was signed, so it has to match exactly.
    request.setRequestHeader("content-type", contentType);
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    });
    request.addEventListener("load", () =>
      request.status >= 200 && request.status < 300
        ? resolve()
        : reject(new ApiError([`Upload failed: ${request.status} ${request.statusText}`])),
    );
    request.addEventListener("error", () => reject(new ApiError(["Upload failed. Check the connection and try again."])));
    request.send(file);
  });

  return key;
}

export async function createJob(
  request: RecognitionRequest,
  auth: AuthHeaders = NO_AUTH,
): Promise<JobResponse> {
  return postJson<JobResponse>("/jobs", request, auth);
}

export async function getJob(jobId: string, auth: AuthHeaders = NO_AUTH): Promise<JobResponse> {
  return json<JobResponse>(await fetch(`${BASE}/jobs/${jobId}`, { headers: await auth() }));
}

/**
 * Polls until the job finishes.
 *
 * Five seconds, and no cleverness. Recognition runs for minutes, and a worker may have to
 * start from zero first - about four more - so a tighter interval only adds requests to a wait
 * whose length is decided elsewhere. `onUpdate` fires on every poll so the interface can say
 * something truer than "working".
 */
export async function awaitJob(
  jobId: string,
  onUpdate: (job: JobResponse) => void,
  signal?: AbortSignal,
  auth: AuthHeaders = NO_AUTH,
): Promise<JobResponse> {
  for (;;) {
    if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
    const job = await getJob(jobId, auth);
    onUpdate(job);
    if (job.status === "succeeded" || job.status === "failed") return job;
    await new Promise((resolve) => setTimeout(resolve, 5000));
  }
}

/** What POST /jobs/{id}/package hands back. The package itself is fetched from `url`. */
export interface PackageResponse {
  readonly key: string;
  readonly url: string;
  readonly fileName: string;
  readonly reviewedBy: string;
  readonly reviewedAtUtc?: string;
}

/**
 * Creates the schema-41 conversion package for a finished job, and returns a URL to it.
 *
 * Everything the package needs about the conversion itself is already on the server, in the
 * job record and the result in S3. What this sends is only what the browser alone knows: the
 * school and district details, the calibration the operator measured, and whether they have
 * confirmed the geometry review.
 */
export async function createPackage(
  jobId: string,
  request: CreatePackageBody,
  auth: AuthHeaders = NO_AUTH,
): Promise<PackageResponse> {
  return postJson<PackageResponse>(`/jobs/${jobId}/package`, request, auth);
}

export interface CreatePackageBody {
  readonly schoolName: string;
  readonly schoolAddress: string;
  readonly districtName: string;
  readonly districtAddress: string;
  readonly portalId: string;
  readonly sourceFileName: string;
  readonly calibrationPoint1: Point;
  readonly calibrationPoint2: Point;
  readonly enteredDistance: string;
  readonly reviewAcknowledged: boolean;
}

/**
 * The recognition result, fetched straight from S3 through the presigned URL.
 *
 * No Authorization header, and that is not an oversight: the URL is already signed, and
 * sending one alongside makes S3 reject the request as having two authentication mechanisms.
 */
export async function fetchResult(resultUrl: string): Promise<RecognitionResult> {
  const response = await fetch(resultUrl);
  if (!response.ok) throw new ApiError([`Could not fetch the result: ${response.status}`]);
  return (await response.json()) as RecognitionResult;
}

/** A presigned package, fetched and parsed so per-area packages can be joined in the browser. */
export async function fetchPackage(url: string): Promise<ConversionPackage> {
  const response = await fetch(url);
  if (!response.ok) throw new ApiError([`Could not fetch the package: ${response.status}`]);
  return (await response.json()) as ConversionPackage;
}

/**
 * The engine's output, as much of it as the overlay draws.
 *
 * **Every shape here was read off a committed corpus baseline, not inferred.** The first
 * version of this file guessed, and guessed wrong in four places: polylines and
 * doorGraphicLines carry `vertices` rather than `points`, splines carry `fitPoints`, and
 * arcLines are arcs - centre, radius and two angles - rather than segments. Drawing code
 * written against the guess threw inside an effect on the first real result, which unmounts
 * the React tree and shows a blank page.
 *
 * services/recognition-worker/RecognitionHarness/corpus/expected/ is the reference. It is
 * committed, it is what the gate asserts, and it costs nothing to look at.
 *
 * Still deliberately partial: the full result carries stage notes and raster audit data the
 * overlay has no use for. Widening this is how the overlay grows.
 */
export interface RecognitionResult {
  readonly engineVersion: string;
  readonly sourceDocumentType: string;
  readonly sourcePixelWidth: number;
  readonly sourcePixelHeight: number;
  readonly wallLines: readonly Segment[];
  /** Reconstructed wall chains on the raster path. `vertices`, not `points`. */
  readonly polylines: readonly VertexChain[];
  readonly arcLines: readonly Arc[];
  /** Architectural curves kept as curves. `fitPoints`, not `points`. */
  readonly splines: readonly Spline[];
  /** The drawing's own door swings, as vertex chains rather than segments. */
  readonly doorGraphicLines: readonly VertexChain[];
  readonly doors: readonly Door[];
  readonly roomTags: readonly RoomTag[];
  readonly textRegions: readonly TextRegion[];
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Segment {
  readonly start: Point;
  readonly end: Point;
  readonly confidence?: number;
}

export interface VertexChain {
  readonly vertices: readonly Point[];
  readonly closed?: boolean;
}

export interface Spline {
  readonly fitPoints: readonly Point[];
}

export interface Arc {
  readonly center: Point;
  readonly radius: number;
  readonly startAngleRadians: number;
  readonly endAngleRadians: number;
  /** Signed. Its sign is the sweep direction, which is what canvas needs to draw the arc. */
  readonly sweepRadians?: number;
}

export interface Door {
  readonly center: Point;
  readonly widthInches: number;
  readonly rotationDegrees?: number;
  readonly blockType?: string;
  readonly confidence?: number;
  /** The drawn swing of each leaf - two for a double door. Absent on older results. */
  readonly swings?: readonly DoorSwingShape[] | null;
}

/** Hinge, closed-leaf tip and open-leaf tip in preview space; the arc runs between the tips. */
export interface DoorSwingShape {
  readonly hinge: Point;
  readonly closedTip: Point;
  readonly openTip: Point;
}

export interface RoomTag {
  readonly position: Point;
  readonly roomNumber: string;
  readonly roomName1: string;
  readonly confidence: number;
}

export interface TextRegion {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly text: string;
}
